import { useMemo, useState, useEffect } from 'react';
import { Voice } from '@/types/voice/voice';
import { VoiceViewTab } from '@/types/voice/favorites';

interface FilterState {
  searchQuery: string;
  category: string;
  accent: string;
  language: string;
  age: string;
  gender: string;
}

interface UseVoiceFilteringProps {
  allVoices: Voice[] | undefined;
  recommendedVoices: Voice[];
  allRecommendedVoices: Voice[];
  favoriteVoices: Voice[];
  activeTab: VoiceViewTab;
}

export const useVoiceFiltering = ({
  allVoices,
  recommendedVoices,
  allRecommendedVoices,
  favoriteVoices,
  activeTab
}: UseVoiceFilteringProps) => {
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    category: 'all',
    accent: 'all',
    language: 'all',
    age: 'all',
    gender: 'all'
  });

  // Pagination state
  const [visibleVoiceCount, setVisibleVoiceCount] = useState(50);

  // Extract filter options from voices
  const filterOptions = useMemo(() => {
    let voices: Voice[] = [];
    if (activeTab === 'all') {
      voices = allVoices || [];
    } else if (activeTab === 'recommended') {
      // For recommended tab, use all accumulated voices for filter options
      voices = allRecommendedVoices.length > 0 ? allRecommendedVoices : recommendedVoices;
    } else {
      voices = recommendedVoices || [];
    }

    if (!voices.length) return { accents: [], ages: [], genders: [], languages: [] as string[] };

    const accents = new Set<string>();
    const ages = new Set<string>();
    const genders = new Set<string>();
    const languages = new Set<string>();

    voices.forEach(voice => {
      if (voice.labels?.accent) accents.add(voice.labels.accent);
      if (voice.labels?.age) ages.add(voice.labels.age);
      if (voice.labels?.gender) genders.add(voice.labels.gender);
      voice.verified_languages?.forEach(l => {
        if (l.language) languages.add(l.language);
      });
    });

    return {
      accents: Array.from(accents).sort(),
      ages: Array.from(ages).sort(),
      genders: Array.from(genders).sort(),
      languages: Array.from(languages).sort(),
    };
  }, [allVoices, recommendedVoices, allRecommendedVoices, activeTab]);

  // Filter voices based on active tab and filters
  const filteredVoices = useMemo(() => {
    let voicesToFilter: Voice[] = [];

    // Select voices based on active tab
    switch (activeTab) {
      case 'recommended':
        voicesToFilter = recommendedVoices;
        break;
      case 'favorites':
        voicesToFilter = favoriteVoices;
        break;
      case 'all':
        voicesToFilter = allVoices || [];
        break;
      default:
        voicesToFilter = recommendedVoices;
    }

    // All Voices stays server-driven. Recommended and Favorites are client-filtered.
    if (activeTab === 'all') {
      return voicesToFilter;
    }

    // For client-side filtered tabs (recommended, favorites), apply all filters.
    const results = voicesToFilter.filter(voice => {
      // Search filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch =
          voice.name.toLowerCase().includes(query) ||
          voice.description?.toLowerCase().includes(query) ||
          voice.category?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Category filter
      if (filters.category !== 'all' && voice.category !== filters.category) {
        return false;
      }

      // Accent filter
      if (filters.accent !== 'all' && voice.labels?.accent !== filters.accent) {
        return false;
      }

      // Age filter
      if (filters.age !== 'all' && voice.labels?.age !== filters.age) {
        return false;
      }

      // Gender filter
      if (filters.gender !== 'all' && voice.labels?.gender !== filters.gender) {
        return false;
      }

      // Language filter
      if (filters.language !== 'all') {
        const langs = voice.verified_languages?.map(l => l.language) || [];
        if (!langs.includes(filters.language)) return false;
      }

      return true;
    });

    // Prioritize featured voices
    results.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return 0;
    });

    return results;
  }, [
    allVoices,
    recommendedVoices,
    favoriteVoices,
    activeTab,
    filters
  ]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleVoiceCount(50);
  }, [activeTab, filters]);

  // Visible voices (for pagination)
  const visibleFilteredVoices = useMemo(() => {
    return filteredVoices.slice(0, visibleVoiceCount);
  }, [filteredVoices, visibleVoiceCount]);

  // Filter update functions
  const updateFilter = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      searchQuery: '',
      category: 'all',
      accent: 'all',
      language: 'all',
      age: 'all',
      gender: 'all'
    });
  };

  const loadMoreVoices = () => {
    setVisibleVoiceCount(prev => prev + 50);
  };

  return {
    filters,
    filterOptions,
    filteredVoices,
    visibleFilteredVoices,
    visibleVoiceCount,
    updateFilter,
    resetFilters,
    loadMoreVoices
  };
};
